import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Button, Spinner } from '@w7/ui'
import {
  clearAllAuth,
  logout,
  rememberNext,
  resolveAuthPhase,
  type AuthPhase,
} from '@/auth/oidc'

/**
 * Gates the authenticated shell. Public routes (/login, /callback, /no-access)
 * render outside this component.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  // Resolve synchronously so pathname changes don’t flash “Checking session…”
  const [phase, setPhase] = useState<AuthPhase>(() => resolveAuthPhase())

  useEffect(() => {
    setPhase(resolveAuthPhase())
  }, [location.pathname, location.search])

  if (phase.phase === 'loading') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-loading">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Checking session…
        </p>
      </div>
    )
  }

  if (phase.phase === 'anon') {
    rememberNext(`${location.pathname}${location.search}`)
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }

  if (phase.phase === 'expired') {
    clearAllAuth()
    return <Navigate to="/login?reason=expired" replace />
  }

  if (phase.phase === 'no-access') {
    return <Navigate to="/no-access" replace />
  }

  if (phase.phase === 'ready') {
    return <>{children}</>
  }

  return (
    <div className="w7-auth w7-auth-center">
      <p>Unexpected auth state.</p>
      <Button onClick={() => logout()}>Sign out</Button>
    </div>
  )
}
